import React, { useState, ReactNode, useRef } from 'react'
import {
  Stack,
  Box,
  Typography,
  Accordion as MUIAccordion,
} from '@mui/material'

interface ItemMenuProps {
  label: string
  last?: boolean
  active?: boolean
  collapsedPopup?: boolean
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const ItemMenu: React.FC<ItemMenuProps> = ({
  label,
  last = false,
  active = false,
  collapsedPopup = false,
  onClick = () => { }
}) => {
  return (
    <Stack direction={'row'} sx={{ minHeight: collapsedPopup ? 26 : 30 }}>
      {!collapsedPopup && (
        <Stack width={'10px'} ml={1.75}>
          {
            last
              ? (
                <>
                  <Stack
                    sx={{
                      height: '16px',
                      width: '12px',
                      border: `0px solid ${active ? '#2570EB' : '#DCDCDC'}`,
                      borderLeftWidth: '1px',
                      borderBottomWidth: '1px',
                      borderBottomLeftRadius: '8px'
                    }}
                  />
                  <Stack
                    sx={{
                      height: '16px'
                    }}
                  />
                </>
              )
              : (
                <Stack
                  sx={{
                    height: '32px',
                    border: `0px solid ${active ? '#2570EB' : '#DCDCDC'}`,
                    borderLeftWidth: '1px'
                  }}
                />
              )
          }
        </Stack>
      )}
      <Stack
        onClick={(e) => onClick(e)}
        sx={{
          fontSize: collapsedPopup ? '11px' : '12px',
          alignSelf: 'center',
          color: collapsedPopup ? '#464646' : active ? '#464646' : '#989898',
          fontWeight: active ? 500 : '400',
          cursor: 'pointer',
          padding: collapsedPopup ? '5px 10px' : '5px 10px',
          width: '100%',
          backgroundColor: active ? '#F7F7F7' : 'transparent',
          borderRadius: active ? '24px' : 0,
          '&:hover': {
            fontWeight: 500,
            backgroundColor: '#F7F7F7',
            borderRadius: '24px'
          }
        }}
      >
        {label}
      </Stack>
    </Stack>
  )
}

export const IconButton = ({
  active = false,
  onClick = () => { },
  onMouseOver = () => { },
  onMouseOut = () => { },
  children
}) => {
  return (
    <Box
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={onClick}
      sx={{
        height: 32,
        width: 32,
        position: 'relative',
        borderRadius: '50%',
        backgroundColor: active ? '#2570EB' : 'inherit',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: active ? '#2570EB' : '#F2F8FD'
        },
        cursor: 'pointer'
      }}>
      {children}
    </Box>
  )
}

interface CategoryCollapseProps {
  label: string | null;
  icon: string;
  iconColor?: string;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode | null;
}

export const CategoryCollapse: React.FC<CategoryCollapseProps> = ({
  label,
  icon,
  iconColor,
  active = false,
  onClick = () => { },
  disabled = false,
  children,
}) => {
  const [showChildren, setShowChildren] = useState(false)
  const timeoutId = useRef<NodeJS.Timeout | null>(null)
  
  const hasChildren = React.Children.count(children) > 0
  const popupBackground = hasChildren ? 'white' : '#292929'
  const popupBorder = hasChildren ? '#EFEFEF' : '#292929'
  const popupChildren = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child
    return React.cloneElement(child as React.ReactElement<ItemMenuProps>, { collapsedPopup: true })
  })
  
  const iconContent = (
    <i
      className={`fa-${active ? 'solid' : 'light'} fa-${icon}`}
      style={{
        color: active ? 'white' : iconColor || '#464646',
        fontSize: 14,
      }}
    />
  )

  const iconButton = (
    <IconButton
      active={active}
      onMouseOver={() => {
        if (timeoutId.current) {
          clearTimeout(timeoutId.current)
          timeoutId.current = null
        }
        setShowChildren(true)
      }}
      onMouseOut={() => {
        timeoutId.current = setTimeout(() => setShowChildren(false), 50)
      }}
    >
      {iconContent}
      {showChildren && !disabled && (
        <Box
          p={hasChildren ? 1.25 : 0.35}
          sx={{
            width: 'max-content',
            maxWidth: 220,
            backgroundColor: popupBackground,
            position: 'absolute',
            borderRadius: '24px',
            border: `1px solid ${popupBorder}`,
            boxShadow: '0px 1.85px 3.15px 0px #00000014, 0px 8.15px 6.52px 0px #0000000A, 0px 20px 13px 0px #0000000A',
            left: 44,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10000,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: hasChildren ? -5.5 : -4.5,
              top: '50%',
              width: hasChildren ? 11 : 9,
              height: hasChildren ? 11 : 9,
              backgroundColor: popupBackground,
              borderLeft: `1px solid ${popupBorder}`,
              borderBottom: `1px solid ${popupBorder}`,
              transform: 'translateY(-50%) rotate(45deg)'
            }
          }}
        >
          {hasChildren ? (
            <Box
              sx={{
                minHeight: '28px',
                borderRadius: '24px',
                backgroundColor: active ? '#2570EB' : '#292929',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1.25,
                position: 'relative'
              }}
            >
              <Typography sx={{ fontSize: 11, lineHeight: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                minHeight: '22px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                fontSize: 11,
                lineHeight: '14px',
                fontWeight: 500,
                position: 'relative',
                whiteSpace: 'nowrap'
              }}
            >
              {label}
            </Typography>
          )}
          {hasChildren && (
            <Box sx={{ mt: 0.75, position: 'relative' }}>
              {popupChildren}
            </Box>
          )}
        </Box>
      )}
    </IconButton>
  )

  return (
    <Stack
      direction={'row'}
      spacing={1.4}
      sx={{
        fontSize: '14px',
        cursor: 'pointer',
        opacity: disabled ? 0.5 : 1
      }}
      alignItems={'center'}
      justifyContent={'center'}
      flex={1}
      onClick={!disabled ? onClick : undefined}
    >
      {iconButton}
    </Stack>
  )
}

interface CategoryExpandProps {
  label: string | null;
  icon: string;
  iconColor?: string;
  active?: boolean;
  isExpanded?: boolean;
  onExpand?: (isChevronClick: boolean) => void;
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode | null;
}

const CategoryExpand: React.FC<CategoryExpandProps> = ({
  label,
  icon,
  iconColor,
  active = false,
  isExpanded = false,
  onExpand,
  onClick = () => { },
  disabled = false,
  children = undefined
}) => {
  const hasChildren = React.Children.count(children) > 0

  const handleLabelClick = () => {
    if (disabled) return
    onClick();
    if (hasChildren) {
      onExpand?.(false);
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    if (disabled) return
    e.stopPropagation();
    onExpand?.(true);
  };

  const labelContent = (
    <MUIAccordion
      expanded={hasChildren && isExpanded}
      onChange={() => !disabled && hasChildren && onExpand?.(true)}
      sx={{
        width: '100%',
        boxShadow: 'none',
        '&::before': {
          height: 0
        },
        '&.Mui-expanded': {
          margin: 0,
          marginTop: '2px'
        },
        opacity: disabled ? 0.5 : 1
      }}
    >
      <Stack
        direction={'row'}
        spacing={1.4}
        p={.5}
        pl={1}
        onClick={handleLabelClick}
        sx={{
          fontSize: '12px',
          cursor: 'pointer',
          flex: 1,
          justifyContent: 'space-between',
          border: '1px solid transparent',
          borderRadius: '24px',
          minHeight: '34px',
          ...(!disabled && {
            '&:hover': {
              backgroundColor: active ? '#2570EB' : '#F2F8FD',
              borderColor: active ? '#2570EB' : '#E4EEFA',
              '& .category-label': {
                fontWeight: 500
              }
            }
          }),
          ...(active && { backgroundColor: '#2570EB', borderColor: '#2570EB' })
        }}
        alignItems={'center'}
      >
        <Stack direction="row" alignItems="center" spacing={2.25} minWidth={0}>
          <Box sx={{ height: 20, width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i
              className={`fa-${active || iconColor ? 'solid' : 'light'} fa-${icon}`}
              style={{ color: active ? 'white' : iconColor || '#464646', fontSize: 15 }}
            />
          </Box>
          <Typography
            className="category-label"
            component={'span'}
            sx={{
              color: active ? 'white' : '#464646',
              fontSize: '12px',
              fontWeight: active ? 500 : 400,
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Typography>
        </Stack>
        {hasChildren && (
          <Stack
            onClick={handleChevronClick}
            sx={{
              cursor: 'pointer',
              color: active ? 'white' : '#DCDCDC',
              '& i': {
                fontSize: '12px'
              }
            }}
            pr={1}
          >
            <i className={`fa-regular fa-angle-${isExpanded ? 'up' : 'down'}`} style={{ alignSelf: 'end' }} />
          </Stack>
        )}
      </Stack>
      {hasChildren && (
        <Stack ml={1} mt={'4px'}>
          {children}
        </Stack>
      )}
    </MUIAccordion>
  )

  return labelContent
}

interface CategoryProps {
  label: string | null;
  icon: string;
  iconColor?: string;
  active?: boolean;
  minimize?: boolean;
  isExpanded?: boolean;
  onExpand?: (isChevronClick: boolean) => void;
  onClick: () => void;
  disabled?: boolean;
  children?: ReactNode | null;
}

const Category: React.FC<CategoryProps> = ({
  active,
  label,
  icon,
  iconColor,
  minimize,
  isExpanded,
  onExpand,
  onClick,
  disabled = false,
  children
}) => {
  if (minimize) {
    return (
      <CategoryCollapse
        label={label}
        icon={icon}
        iconColor={iconColor}
        active={active}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </CategoryCollapse>
    )
  }
  return (
    <CategoryExpand
      active={active}
      label={label}
      icon={icon}
      iconColor={iconColor}
      isExpanded={isExpanded}
      onExpand={onExpand}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </CategoryExpand>
  )
}

export default Category
